import { Box, CircularProgress, Paper, Stack } from "@mui/material";
import {
  ScrapEyesEventWithReviewData,
  useManualReviewEvents,
} from "components/manualReview/contexts/ManualReviewEventContext";
import { ManualReviewVideoExceptionalBars } from "components/manualReview/ManualReviewVideoExceptionalBars";
import { ManualReviewVideoPeriodBars } from "components/manualReview/ManualReviewVideoPeriodBars";
import { ManualReviewVideoReadOnlyShading } from "components/manualReview/ManualReviewVideoReadOnlyShading";
import {
  flagCrossPeriodPairs,
  flagWindowEdgePairs,
  getNextEvent,
  reviewPeriodContaining,
} from "components/manualReview/utils/formatters";
import { maxSequenceGapSecondsFromChunkSeconds } from "components/manualReview/utils/editScope";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import { AdjacentPeriodPreview } from "components/manualReview/AdjacentPeriodPreview";
import { PlaybackControls } from "components/scrapEyeDataDashboard/VideoPlayer/components/PlaybackControls";
import { VideoTimeline } from "components/scrapEyeDataDashboard/VideoPlayer/components/VideoTimeline";
import { useVideoControl } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { AreaMaterialSnapshot } from "components/scrapEyeLabelling/types";
import dayjs, { Dayjs } from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import utc from "dayjs/plugin/utc";
import { SKIP_BUTTON_SECONDS } from "components/scrapEyeDataDashboard/VideoPlayer/constants";
import { useSkipAndStepHandlers } from "components/scrapEyeDataDashboard/VideoPlayer/hooks/useSkipAndStepHandlers";

dayjs.extend(isSameOrAfter);
dayjs.extend(utc);
import { ReactNode, useCallback, useMemo } from "react";
import { LabellerTimeline, ReviewPeriodData } from "src/store/api/generatedApi";
import { LabellerVideoTimelines } from "./LabellerVideoTimelines";
import { hasConflictReasons } from "./utils/reviewReasons";

// How far either side of the period boundary to show in an edge pair's preview — enough
// to reach the partner in the neighbouring period without crowding the events.
const PEEK_MINUTES = 6;

interface VideoControlsProps {
  events: ScrapEyesEventWithReviewData[];
  location?: string;
  reviewPeriods: ReviewPeriodData[];
  selectedEventId?: string;
  recordingEventId?: string;
  onEventClick?: (eventId: ScrapEyesEventWithReviewData["id"]) => void;
  videoStart: Dayjs;
  actionButtons?: ReactNode;
  duration: number;
  onConflictClick?: (eventId: string) => void;
  labellerTimelines: LabellerTimeline[];
  areaMaterialSnapshots?: AreaMaterialSnapshot[];
  onMappingSnapshotClick?: (snapshot: AreaMaterialSnapshot) => void;
  selectedSnapshotTimestamp?: number | null;
  isLoading?: boolean;
  isEditMode?: boolean;
  bringInWillVerifyPeriodIds?: readonly number[];
  highlightColorsByEventId?: ReadonlyMap<string, string | null>;
}

export const ManualReviewVideoControls = ({
  events,
  location,
  reviewPeriods,
  selectedEventId,
  recordingEventId,
  onEventClick,
  videoStart,
  actionButtons,
  duration,
  onConflictClick,
  labellerTimelines,
  areaMaterialSnapshots,
  onMappingSnapshotClick,
  selectedSnapshotTimestamp,
  isLoading,
  isEditMode,
  bringInWillVerifyPeriodIds,
  highlightColorsByEventId,
}: VideoControlsProps) => {
  const { state, actions } = useVideoControl();
  const { allEvents, modifiedEventIds } = useManualReviewEvents();
  const { reservation } = useScrapEyeMetadata();
  const maxSequenceGapSeconds = maxSequenceGapSecondsFromChunkSeconds(
    reservation.chunk_seconds
  );

  // Flag boundary-crossing pairs over the whole window (not the zoom-filtered
  // subset, or a pair with one end scrolled off would lose its flag): pairs split
  // across review periods, and unpaired ends whose partner lies beyond the window.
  const flaggedById = useMemo(() => {
    const flagged = flagWindowEdgePairs({
      events: flagCrossPeriodPairs({
        events: allEvents,
        contextEvents: [],
        reviewPeriods,
        maxSequenceGapSeconds,
      }),
      contextEvents: [],
      maxSequenceGapSeconds,
    });
    return new Map(flagged.map((event) => [event.id, event]));
  }, [allEvents, reviewPeriods, maxSequenceGapSeconds]);

  const timelineEvents = useMemo(
    () => events.map((event) => flaggedById.get(event.id) ?? event),
    [events, flaggedById]
  );

  // An unpaired end whose partner isn't loaded runs past a review-period boundary, so
  // preview the region on both sides of that boundary in the usual timeline, fetched on
  // hover: a dangling lift's drop lies in the next period (boundary = its period's end),
  // a dangling drop's lift in the previous period (boundary = its period's start). Both
  // sides get PEEK_MINUTES of context so the in-view end and its off-window partner are
  // shown together, split at the period boundary. Falls back to the window edge for an
  // event outside every materialised period.
  const adjacentPreviewById = useMemo(() => {
    if (location === undefined) return new Map<string, ReactNode>();
    const windowStart = videoStart;
    const windowEnd = videoStart.add(duration, "second");
    return new Map<string, ReactNode>(
      timelineEvents
        .filter((event) => event.pairPartnerBeyondWindow)
        .map((event): [string, ReactNode] => {
          const isDrop = event.eventType === "drop";
          const period = reviewPeriodContaining(event.start, reviewPeriods);
          const boundary = isDrop
            ? period
              ? dayjs.utc(period.start)
              : windowStart
            : period
              ? dayjs.utc(period.end)
              : windowEnd;
          const peekStart = boundary.subtract(PEEK_MINUTES, "minute");
          const peekEnd = boundary.add(PEEK_MINUTES, "minute");
          const start = event.start.isBefore(peekStart)
            ? event.start
            : peekStart;
          const end = event.end.isAfter(peekEnd) ? event.end : peekEnd;
          return [
            event.id,
            <AdjacentPeriodPreview
              key={event.id}
              location={location}
              start={start}
              end={end}
              boundary={boundary}
              inViewSide={isDrop ? "after" : "before"}
            />,
          ];
        })
    );
  }, [timelineEvents, videoStart, duration, location, reviewPeriods]);

  const modifiedPeriodIds = useMemo(() => {
    if (!isEditMode || modifiedEventIds.size === 0) return new Set<number>();
    const modifiedEvents = events.filter((e) => modifiedEventIds.has(e.id));
    return new Set(
      reviewPeriods
        .filter((period) => {
          const periodStart = dayjs(period.start);
          const periodEnd = dayjs(period.end);
          return modifiedEvents.some(
            (event) =>
              event.start.isSameOrAfter(periodStart) &&
              event.start.isBefore(periodEnd)
          );
        })
        .map((period) => period.id)
    );
  }, [isEditMode, modifiedEventIds, events, reviewPeriods]);

  const {
    handleSkipBackward,
    handleSkipForward,
    handleStepBackward,
    handleStepForward,
  } = useSkipAndStepHandlers({ skipSeconds: SKIP_BUTTON_SECONDS });

  const nextEvent = useMemo(
    () =>
      getNextEvent(events, videoStart.add(state.currentTime, "second").unix()),
    [events, videoStart, state.currentTime]
  );

  const handleEventClick = useCallback(
    (eventId: string) => {
      const event = events.find((e) => e.id === eventId);
      if (
        event &&
        onConflictClick &&
        (hasConflictReasons(event.review_reasons) || event.resolved)
      ) {
        onConflictClick(eventId);
      } else {
        onEventClick?.(eventId);
      }
    },
    [events, onConflictClick, onEventClick]
  );

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Stack spacing={2}>
        {/* The merged timeline and the labeller lanes both restate the merge, so a fetch that
            replaces it covers them together — the lanes' conflict marks and merged-event links
            are as stale as the bars above them. */}
        <Box sx={{ position: "relative" }}>
          <Stack spacing={2}>
            <VideoTimeline
              location={location}
              currentTime={state.currentTime}
              duration={state.duration}
              events={timelineEvents}
              adjacentPreviewById={adjacentPreviewById}
              selectedEventId={selectedEventId}
              onSeek={actions.seekTo}
              onEventClick={handleEventClick}
              videoStart={videoStart}
              areaMaterialSnapshots={areaMaterialSnapshots}
              onMappingSnapshotClick={onMappingSnapshotClick}
              selectedSnapshotTimestamp={selectedSnapshotTimestamp}
              modifiedEventIds={modifiedEventIds}
              highlightColorsByEventId={highlightColorsByEventId}
            >
              <ManualReviewVideoExceptionalBars videoStart={videoStart} />
              <ManualReviewVideoPeriodBars
                reviewPeriods={reviewPeriods}
                videoStart={videoStart}
                isEditMode={isEditMode}
                modifiedPeriodIds={modifiedPeriodIds}
                bringInWillVerifyPeriodIds={bringInWillVerifyPeriodIds}
              />
            </VideoTimeline>

            <LabellerVideoTimelines
              mergedEvents={events}
              location={location}
              labellerTimelines={labellerTimelines}
              videoStart={videoStart}
              currentTime={state.currentTime}
              duration={duration}
            />
          </Stack>

          <ManualReviewVideoReadOnlyShading videoStart={videoStart} />

          {isLoading === true && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.6)",
                zIndex: 1,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>

        <PlaybackControls
          isPlaying={state.isPlaying}
          playbackRate={state.playbackRate}
          onTogglePlayPause={actions.togglePlayPause}
          onSkipBackward={handleSkipBackward}
          onSkipForward={handleSkipForward}
          onSpeedChange={actions.setPlaybackRate}
          isRecording={!!recordingEventId}
          focusedEvent={nextEvent}
          actionButtons={actionButtons}
          videoStart={videoStart}
          showDraggableEventWindow
          onStepBackward={handleStepBackward}
          onStepForward={handleStepForward}
        />
      </Stack>
    </Paper>
  );
};
